import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useKey, usePrevious } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Burger from '@/components/base/burger'
import CtaButton from '@/components/base/cta-button'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import LangSwitcher from '@/components/layout/lang-switcher'
import Menu from '@/components/layout/menu'
import SearchOverlay from '@/components/layout/search-overlay'
import { useLenisStore } from '@/contexts/lenis/use-lenis-store'
import { useStore } from '@/contexts/store'
import useStableViewport from '@/hooks/use-stable-viewport'
import { useHomeHeroStore } from '@/store/home-hero'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { useRouterStore } from '@/store/router'
import { pageHasHero } from '@/utils/page'
import { stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type HeaderProps = HTMLAttributes<HTMLDivElement> & {
  pageProps: WPPageData
}

const SUBNAV_LEFT_OFFSET = 190
const SUBNAV_CLOSE_DELAY = 200

const Header = ({ className, pageProps, ...props }: HeaderProps) => {
  const { strings, menu } = useStore()
  const router = useRouter()
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const homeIntroRunning = useHomeHeroStore((state) => state.homeIntroRunning)
  const { scroll } = useLenisStore()
  const prevScroll = usePrevious(scroll) || 0
  const { isMobile } = useStableViewport()

  const [headerHidden, setHeaderHidden] = useState(false)
  const [subNavOpen, setSubNavOpen] = useState(false)
  const [hoveredSubNavIndex, setHoveredSubNavIndex] = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)

  const headerRef = useRef<HTMLElement>(null)
  const openSubNavRef = useRef<HTMLAnchorElement>(null)
  const subNavRef = useRef<HTMLDivElement>(null)
  const collectionImageRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurRafRef = useRef<number | null>(null)
  const isOverTriggerRef = useRef(false)
  const isOverPanelRef = useRef(false)

  const shouldShowSubNav = subNavOpen && !headerHidden && !menuOpen

  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  const darkModeByHero = useMemo(
    () =>
      !menuOpen &&
      hasHero &&
      typeof scroll === 'number' &&
      scroll < 100 &&
      pageProps?.type !== 'product',
    [menuOpen, hasHero, scroll, pageProps]
  )

  const isActive = useCallback(
    (url: string) =>
      stripSlash(url) ===
      stripSlash(
        `${currentLocale !== router.defaultLocale ? `/${currentLocale}` : ''}${router.asPath}`
      ),
    [router, currentLocale]
  )

  // ── Timer helpers ──

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      if (!isOverTriggerRef.current && !isOverPanelRef.current) {
        setSubNavOpen(false)
        clearCloseTimer()
      }
    }, SUBNAV_CLOSE_DELAY)
  }, [clearCloseTimer])

  const closeSubNavImmediately = useCallback(() => {
    clearCloseTimer()
    isOverTriggerRef.current = false
    isOverPanelRef.current = false
    setSubNavOpen(false)
  }, [clearCloseTimer])

  // ── Trigger (openSubNavRef) events ──

  const openSubNav = useCallback(() => {
    if (headerHidden || menuOpen) return
    clearCloseTimer()
    isOverTriggerRef.current = true
    setSubNavOpen(true)
  }, [headerHidden, menuOpen, clearCloseTimer])

  const leaveTrigger = useCallback(() => {
    isOverTriggerRef.current = false
    scheduleClose()
  }, [scheduleClose])

  // ── Panel (subNavRef) events ──

  const enterPanel = useCallback(() => {
    isOverPanelRef.current = true
    clearCloseTimer()
  }, [clearCloseTimer])

  const leavePanel = useCallback(() => {
    isOverPanelRef.current = false
    scheduleClose()
  }, [scheduleClose])

  // ── Sibling nav items: close immediately on hover ──

  const onSiblingEnter = useCallback(() => {
    closeSubNavImmediately()
  }, [closeSubNavImmediately])

  // ── Positioning ──

  const updateSubNavPosition = useCallback(() => {
    const trigger = openSubNavRef.current
    const panel = subNavRef.current
    const header = headerRef.current
    if (!trigger || !panel || !header) return

    const triggerLi = trigger.closest('li')
    if (!triggerLi) return

    const triggerRect = triggerLi.getBoundingClientRect()
    const headerRect = header.getBoundingClientRect()

    panel.style.left = `${triggerRect.left - SUBNAV_LEFT_OFFSET}px`
    panel.style.top = `${headerRect.bottom + 1}px`
  }, [])

  // ── Keyboard ──

  useKey('Escape', () => {
    setLayoutState({ menuOpen: false })
    closeSubNavImmediately()
  })

  // ── Effects ──

  // Lock scroll when menu is open
  useEffect(
    () => setLayoutState({ lockScroll: menuOpen }),
    [menuOpen, setLayoutState]
  )

  // Close everything on route change
  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(() => {
      requestAnimationFrame(() => {
        gsap.delayedCall(0.3, () => {
          setLayoutState({ menuOpen: false })
        })
        setHeaderHidden(false)
        closeSubNavImmediately()
      })
    })
    return unsubscribe
  }, [closeSubNavImmediately, setLayoutState])

  // Header show/hide on scroll direction
  useEffect(() => {
    if (!scroll) return

    if (scroll > prevScroll && scroll > 0) {
      setHeaderHidden(true)
      closeSubNavImmediately()
    } else if (scroll < prevScroll) {
      setHeaderHidden(false)
    }
  }, [scroll, prevScroll, closeSubNavImmediately])

  // Close subNav when header hides or menu opens
  useEffect(() => {
    if (headerHidden || menuOpen) {
      closeSubNavImmediately()
    }
  }, [headerHidden, menuOpen, closeSubNavImmediately])

  useEffect(() => {
    const classToggle = headerHidden ? 'add' : 'remove'
    document.documentElement.classList[classToggle]('header-hidden')
  }, [headerHidden])

  // Animate subNav visibility
  useGSAP(
    () => {
      if (!subNavRef.current) return
      gsap.to(subNavRef.current, {
        opacity: shouldShowSubNav ? 1 : 0,
        duration: shouldShowSubNav ? 0.3 : 0.2,
        pointerEvents: shouldShowSubNav ? 'auto' : 'none'
      })
    },
    { dependencies: [shouldShowSubNav] }
  )

  // Animate collection image slider
  useGSAP(
    () => {
      if (!collectionImageRef.current) return
      gsap.to(collectionImageRef.current, {
        y: -hoveredSubNavIndex * 78,
        duration: 0.4,
        ease: 'power2.out'
      })
    },
    { dependencies: [hoveredSubNavIndex] }
  )

  // Keep subNav position synced with openSubNavRef.
  // ResizeObserver on the header catches CSS height transitions
  // (e.g. h-header-height ↔ h-header-height-contained) frame-by-frame.
  useEffect(() => {
    if (!shouldShowSubNav) return

    const header = headerRef.current
    if (!header) return

    updateSubNavPosition()

    const ro = new ResizeObserver(() => {
      updateSubNavPosition()
    })
    ro.observe(header)

    return () => {
      ro.disconnect()
    }
  }, [shouldShowSubNav, updateSubNavPosition])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearCloseTimer()
      if (blurRafRef.current !== null) {
        cancelAnimationFrame(blurRafRef.current)
      }
    }
  }, [clearCloseTimer])

  // ── Accessibility: focus/blur on subNav panel ──

  const onPanelFocusCapture = useCallback(() => {
    isOverPanelRef.current = true
    clearCloseTimer()
  }, [clearCloseTimer])

  const onPanelBlurCapture = useCallback(() => {
    blurRafRef.current = requestAnimationFrame(() => {
      const active = document.activeElement
      const insidePanel =
        !!subNavRef.current && !!active && subNavRef.current.contains(active)
      const insideTrigger =
        !!openSubNavRef.current &&
        !!active &&
        openSubNavRef.current.contains(active)

      isOverPanelRef.current = insidePanel

      if (!insidePanel && !insideTrigger && !isOverTriggerRef.current) {
        scheduleClose()
      }
    })
  }, [scheduleClose])

  return (
    <>
      <header
        ref={headerRef}
        className={cn(
          'fixed left-0 right-0 top-0 z-header',
          scroll === 0 ? 'h-header-height' : 'h-header-height-contained',
          'transition-all duration-300 ease-in-out-cubic',
          scroll === 0 && !menuOpen
            ? 'bg-transparent'
            : 'bg-surface-primary-dark/20 backdrop-blur-[17px] dark:bg-surface-primary-light/20',
          {
            dark: darkModeByHero,
            '-translate-y-full': headerHidden,
            'opacity-0 [&>*]:!pointer-events-none': homeIntroRunning
          },
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'flex h-full items-end px-padding-sm py-spacing-xs md:px-[3rem] lg:px-[6rem] xl:px-padding-lg',
            'text-text-heading-invert-dark dark:text-text-heading-invert-light'
          )}
        >
          <div>
            <Link
              href="/"
              scroll={false}
              className={cn(
                'block overflow-hidden text-text-heading-invert-dark dark:text-text-heading-invert-light'
              )}
            >
              <span className="sr-only">{strings?.brand_name}</span>
              <SVGIcon
                symbol="logo"
                className={cn(
                  'h-[16px] w-[116px] fill-current',
                  'translate-y-full transition-transform duration-700 ease-in-out-cubic',
                  {
                    'translate-y-0': !homeIntroRunning
                  }
                )}
              />
            </Link>
          </div>
          <nav
            aria-label={
              strings?.labels?.main_navigation || 'Navigazione principale'
            }
            className={cn(
              'hidden pl-spacing-3xl lg:block',
              'transition-opacity duration-300 ease-in-out-cubic',
              {
                'pointer-events-none opacity-0': menuOpen,
                'pointer-events-auto opacity-100': !menuOpen
              }
            )}
          >
            <ul className="flex items-end gap-spacing-sm">
              {menu?.main_menu_05?.menu?.map((item, i) => (
                <li
                  key={`${item.id}-${i}`}
                  className="relative translate-y-[2px] overflow-hidden pb-[2px] pt-[5px]"
                >
                  <Link
                    ref={i === 0 ? openSubNavRef : null}
                    onPointerEnter={i === 0 ? openSubNav : onSiblingEnter}
                    onPointerLeave={i === 0 ? leaveTrigger : undefined}
                    onFocus={i === 0 ? openSubNav : onSiblingEnter}
                    onBlur={i === 0 ? leaveTrigger : undefined}
                    href={item.url || '#'}
                    target={item.target || '_self'}
                    scroll={false}
                    {...(i === 0 && {
                      'aria-expanded': shouldShowSubNav,
                      'aria-controls': 'subnav-collection-panel'
                    })}
                    className={cn(
                      'hover:opacity-50',
                      'transition-[opacity,color,transform]',
                      '[transition-duration:300ms,300ms,700ms]',
                      '[transition-timing-function:var(--ease-in-out),var(--ease-in-out),var(--ease-in-out-cubic)]',
                      'font-[500]',
                      'block',
                      'translate-y-[110%]',
                      {
                        'underline underline-offset-[3px]': isActive(item.url),
                        'translate-y-0': !homeIntroRunning,
                        'pointer-events-none': homeIntroRunning
                      }
                    )}
                    style={{
                      transitionDelay: `0s, 0s, ${(i + 1) * 0.05}s`
                    }}
                  >
                    <Typography variant="regular" className="flex">
                      {item.title}
                    </Typography>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className={cn('mb-[-8px] flex flex-grow justify-end')}>
            <div
              className={cn(
                'mr-[10px] flex h-[32px] translate-y-[1px] items-center',
                'overflow-hidden transition-[overlay] delay-500',
                {
                  'pointer-events-none opacity-0': menuOpen,
                  'pointer-events-auto opacity-100': !menuOpen,
                  'overflow-visible': !homeIntroRunning
                }
              )}
            >
              <LangSwitcher
                className={cn(
                  'translate-y-[110%] transition-[transform] duration-700 ease-in-out-cubic',
                  {
                    '!translate-y-0': !homeIntroRunning
                  }
                )}
                style={{
                  transitionDelay: `${isMobile ? 0.05 : (menu?.main_menu_05?.menu?.length || 1 + 1) * 0.05}s`
                }}
              />
            </div>
            <button
              className={cn(
                'mr-[-14px] block size-[48px] h-[32px] lg:hidden',
                'overflow-hidden',
                'ease-in-out transition-opacity duration-300',
                'hover:opacity-50'
              )}
              aria-label={strings?.labels?.grid || 'Griglia'}
              onClick={() => {
                menu?.main_menu_02?.menu?.find((item) =>
                  item.classes?.includes('all')
                )?.url &&
                  router.push(
                    menu.main_menu_02.menu.find((item) =>
                      item.classes?.includes('all')
                    )!.url!
                  )
              }}
            >
              <SVGIcon
                symbol="grid"
                className={cn(
                  'size-[24px] fill-current',
                  'block',
                  'translate-y-[110%] transition-transform duration-700 ease-in-out-cubic',
                  {
                    '!translate-y-0': !homeIntroRunning
                  }
                )}
                style={{
                  transitionDelay: `${isMobile ? 0.05 : (menu?.main_menu_05?.menu?.length || 1 + 1) * 0.05}s`
                }}
              />
            </button>

            <button
              className={cn(
                'hidden size-[48px] h-[32px] items-center justify-center overflow-hidden md:flex',
                'ease-in-out transition-opacity duration-300',
                'hover:opacity-50'
              )}
              aria-label={strings?.labels?.search || 'Cerca'}
              onClick={() => setSearchOpen(true)}
            >
              <SVGIcon
                symbol="search"
                className={cn(
                  'size-[24px] fill-current',
                  'block',
                  'translate-y-[110%] transition-transform duration-700 ease-in-out-cubic',
                  {
                    '!translate-y-0': !homeIntroRunning
                  }
                )}
                style={{
                  transitionDelay: `${isMobile ? 0.05 : (menu?.main_menu_05?.menu?.length || 1 + 2) * 0.05}s`
                }}
              />
            </button>

            <Link
              href={
                strings?.labels?.link_sales_network?.url || '/sales-network'
              }
              scroll={false}
              className={cn(
                'flex size-[48px] h-[32px] items-center justify-center overflow-hidden md:hidden',
                'ease-in-out transition-opacity duration-300',
                'hover:opacity-50'
              )}
              aria-label={
                strings?.labels?.link_sales_network?.title || 'Rete di vendita'
              }
            >
              <SVGIcon
                symbol="globe"
                className={cn(
                  'size-[28px] fill-current',
                  'block',
                  'translate-y-[110%] transition-transform duration-700 ease-in-out-cubic',
                  {
                    '!translate-y-0': !homeIntroRunning
                  }
                )}
                style={{
                  transitionDelay: `${isMobile ? 0.05 : (menu?.main_menu_05?.menu?.length || 1 + 2) * 0.05}s`
                }}
              />
            </Link>

            <div className="h-[32px] translate-y-[-8px] overflow-hidden">
              <Burger
                className={cn(
                  'mt-[-1px] md:mt-0',
                  'block',
                  'transition-timing-function-[var(--ease-in-out),var(--ease-in-out-cubic)] translate-y-[110%] transition-[opacity,transform] [transition-duration:300ms,700ms]',
                  'hover:opacity-50',
                  {
                    '!translate-y-0': !homeIntroRunning
                  }
                )}
                style={{
                  transitionDelay: `0s, ${isMobile ? 0.05 : (menu?.main_menu_05?.menu?.length || 1 + 3) * 0.05}s`
                }}
                onClick={() => {
                  closeSubNavImmediately()
                  setLayoutState({ menuOpen: !menuOpen })
                }}
                open={menuOpen}
              />
            </div>
          </div>
        </div>
      </header>
      <div
        id="subnav-collection-panel"
        data-subnav-collection
        ref={subNavRef}
        role="region"
        aria-label={strings?.labels?.collections_panel || 'Pannello collezioni'}
        aria-hidden={!shouldShowSubNav}
        className={cn(
          'pointer-events-none opacity-0',
          'fixed z-header flex w-[560px] gap-spacing-2xs bg-surface-primary-dark/20 p-spacing-2xs backdrop-blur-[17px] dark:bg-surface-primary-light/20',
          {
            '!pointer-events-none !opacity-0': homeIntroRunning
          }
        )}
        onPointerEnter={enterPanel}
        onPointerLeave={leavePanel}
        onFocusCapture={onPanelFocusCapture}
        onBlurCapture={onPanelBlurCapture}
      >
        <div className="relative h-[78px] flex-[0_0_166px] overflow-hidden">
          <div
            ref={collectionImageRef}
            className="flex flex-col"
            data-subnav-collection-image
          >
            {menu?.main_menu_02?.menu?.map((item, index) => (
              <Link
                key={`${item.id}-${index}`}
                href={item.url || '#'}
                target={item.target || '_self'}
                scroll={false}
                className="relative block aspect-[166/78]"
                tabIndex={-1}
                aria-hidden="true"
              >
                <img
                  src={item.acf?.image?.sizes?.medium || '/images/kitchen.jpg'}
                  className="absolute left-0 top-0 h-full w-full object-cover"
                  alt=""
                />
              </Link>
            ))}
          </div>
        </div>
        <nav
          data-subnav-collection-nav
          aria-label={strings?.labels?.collections_navigation || 'Collezioni'}
        >
          <ul className="flex flex-col flex-nowrap lg:flex-row lg:flex-wrap">
            {menu?.main_menu_02?.menu?.map((item, index) => (
              <li
                key={`${item.id}-${index}`}
                className={cn('flex-[0_0_50%] px-spacing-2xs', {
                  hidden: item.classes?.includes('all')
                })}
              >
                <CtaButton
                  href={item.url || '#'}
                  target={item.target || '_self'}
                  variant="link"
                  className={cn(
                    'border-b-[2px] border-b-[rgba(0,0,0,.2)] py-[4px] dark:border-b-[rgba(255,255,255,.2)] [&_svg]:w-[16px] [&_svg]:translate-y-[2px]'
                  )}
                  onMouseEnter={() => setHoveredSubNavIndex(index)}
                >
                  {item.title}
                </CtaButton>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <Menu />
    </>
  )
}

export default Header
